import * as React from 'react'
import Box from '@mui/material/Box'
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView'
import { TreeItem } from '@mui/x-tree-view/TreeItem'
import { BaseRequirementGroupedByScopes, Scope } from '@shared/types'
import makeStyles from '@mui/styles/makeStyles'
import { useQuery } from 'react-query'
import { apiJSON } from '@shared/utils/api/api.util'
import { Dispatch, ReactNode, SetStateAction, useMemo } from 'react'
import { BackdropProgress } from '@shared/components/backdrop-progress.component'
import { uniq } from 'lodash'
import CompaniesSelectToRequirementsMappingComponent from '@modules/select-scopes/companies-select-to-requirements-mapping.component'

interface Props {
  selected: number[]
  setSelected: Dispatch<SetStateAction<number[]>>
  setDeselectedScopes: Dispatch<
    SetStateAction<{ scopeId: number; excludedRequirements: number[] }[]>
  >
  header: ReactNode
  type: 'requirements' | 'localization'
  scopes: Scope[]
  companyProfile: {
    scopesInEsqula: {
      groups: { requirements: { requirementBaseId: number }[] }[]
    }[]
  }
  setFetchedBaseRequirements: Dispatch<
    SetStateAction<BaseRequirementGroupedByScopes[]>
  >
}

enum TreeItemType {
  SCOPE = 'scope',
  GROUP = 'group',
  REQUIREMENT = 'requirement',
}

export default function RequirementsTree({
  scopes,
  selected,
  setSelected,
  setDeselectedScopes,
  header,
  type,
  companyProfile,
  setFetchedBaseRequirements,
}: Props) {
  const classes = useStyles()

  const { data: baseRequirements, isLoading: areRequirementsFetching } =
    useQuery<BaseRequirementGroupedByScopes[]>(
      'RequirementBase/groupedByScopes',
      () => apiJSON('RequirementBase/groupedByScopes')
    )

  const transformData = (data: BaseRequirementGroupedByScopes[]) => {
    return data?.map((item) => {
      return item.groupedRequirementBases.map((group) => {
        return {
          scope: {
            scopeID: group.scopeId,
            name: item.name,
          },
          groupID: group.id,
          name: group.name,
          requirements: group.baseRequirements.map((req) => {
            return {
              requirementBaseID: req.id,
              name: req.name,
              groupID: group.id,
              group: {
                groupID: group.id,
                name: group.name,
              },
            }
          }),
        }
      })
    })
  }

  const formattedBaseRequirements = React.useMemo(() => {
    return baseRequirements ? transformData(baseRequirements) : []
  }, [baseRequirements])

  const handleItemSelectionToggle = (
    event: React.SyntheticEvent,
    itemId: string,
    isSelected: boolean
  ) => {
    if (itemId.includes(TreeItemType.SCOPE)) {
      const scopeId = +itemId.split('-')[1]
      setSelected((prevItems) => {
        let result = prevItems
        const groupsInScope = formattedBaseRequirements.find(
          (groups) => groups[0].scope?.scopeID === scopeId
        )
        const requirementsIds =
          groupsInScope
            ?.map((group) =>
              group.requirements.map(
                (requirement) => requirement.requirementBaseID
              )
            )
            .flat() || []

        if (isSelected) {
          result = [...result, ...requirementsIds]
        } else {
          result = result.filter((item) => !requirementsIds.includes(item))
        }

        return uniq(result)
      })
      return
    }

    if (itemId.includes(TreeItemType.GROUP)) {
      const groupId = +itemId.split('-')[1]
      const foundGroup = formattedBaseRequirements
        .flat()
        .find((groups) => groups.groupID === groupId)

      if (foundGroup) {
        const requirementsIds = foundGroup.requirements.map(
          (requirement) => requirement.requirementBaseID
        )

        setSelected((prevItems) => {
          return uniq(
            isSelected
              ? [...prevItems, ...requirementsIds]
              : prevItems.filter((id) => !requirementsIds.includes(id))
          )
        })
      }

      return
    }

    if (itemId.includes(TreeItemType.REQUIREMENT)) {
      const requirementId = +itemId.split('-')[1]
      setSelected((prevItems) =>
        uniq(
          isSelected
            ? [...prevItems, requirementId]
            : prevItems.filter((id) => id !== requirementId)
        )
      )
      return
    }
  }

  const isDataLoading = areRequirementsFetching

  const selectedItems = useMemo(() => {
    if (isDataLoading) {
      return []
    }

    const result = selected.map(
      (itemID) => `${TreeItemType.REQUIREMENT}-${itemID}`
    )
    scopes.forEach((scope) => {
      const groups =
        formattedBaseRequirements.find(
          (groups) => groups[0].scope?.scopeID === scope?.scopeID
        ) || []
      const requirementsInScope: number[] = []
      groups.forEach((group) => {
        const requirementsIDs = group.requirements.map(
          (requirement) => requirement.requirementBaseID
        )
        requirementsInScope.push(...requirementsIDs)
        if (requirementsIDs.every((id) => selected.includes(id))) {
          result.push(`${TreeItemType.GROUP}-${group.groupID}`)
        }
      })
      if (requirementsInScope.every((id) => selected.includes(id))) {
        result.push(`${TreeItemType.SCOPE}-${scope?.scopeID}`)
      }
    })

    return result
  }, [selected, scopes, formattedBaseRequirements, isDataLoading])

  React.useEffect(() => {
    if (!isDataLoading && scopes.length > 0) {
      const scopesList = scopes.map((scope) => scope?.scopeID)

      const allowedGroups = formattedBaseRequirements
        .filter((groups) => scopesList.includes(groups[0].scope?.scopeID))
        .flat()

      const groupRequirements = allowedGroups.flatMap((group) =>
        group.requirements.map((req) => req.requirementBaseID)
      )

      const currentCompanyRequirements = companyProfile?.scopesInEsqula.flatMap(
        (scope) =>
          scope?.groups.flatMap((group) =>
            group.requirements.map(
              (requirement) => requirement.requirementBaseId
            )
          )
      )

      const selectedCompanyRequirements: number[] =
        currentCompanyRequirements.filter((id: number) =>
          groupRequirements.includes(id)
        )

      const requirementsToAdd: number[] = currentCompanyRequirements.filter(
        (id: number) => selectedCompanyRequirements.includes(id)
      )

      const scopesInEsqula = allowedGroups
        .map((group) => {
          const scopeId = group?.scope?.scopeID

          const excludedRequirements = group.requirements
            .map((req) => req.requirementBaseID)
            .filter((id) => !requirementsToAdd.includes(id))

          return excludedRequirements.length > 0
            ? { scopeId, excludedRequirements }
            : null
        })
        .filter(Boolean)

      const groupedScopes = scopesInEsqula.reduce<
        { scopeId: number; excludedRequirements: number[] }[]
      >((acc, item) => {
        if (!item) return acc

        const existingScope = acc.find(
          (scope) => scope?.scopeId === item?.scopeId
        )

        if (existingScope) {
          existingScope.excludedRequirements = [
            ...Array.from(
              new Set([
                ...existingScope.excludedRequirements,
                ...item.excludedRequirements,
              ])
            ),
          ]
        } else {
          acc.push(item)
        }

        return acc
      }, [])

      setDeselectedScopes?.(groupedScopes)
      setSelected(uniq(currentCompanyRequirements))
      setFetchedBaseRequirements(baseRequirements ?? [])
    }
  }, [isDataLoading, areRequirementsFetching])

  React.useEffect(() => {
    const scopesList = scopes.map((scope) => scope?.scopeID)

    const allowedGroups = formattedBaseRequirements
      .filter((groups) => scopesList.includes(groups[0]?.scope?.scopeID))
      .flat()

    const result = allowedGroups.map((group) => {
      const scopeId = group?.scope?.scopeID

      const requirementIds = group.requirements.map(
        (req) => req.requirementBaseID
      )

      const excludedRequirements = requirementIds.filter(
        (id) => !selected.includes(id)
      )

      return {
        scopeId,
        excludedRequirements,
      }
    })

    const groupedScopes = result.reduce<
      { scopeId: number; excludedRequirements: number[] }[]
    >((acc, item) => {
      if (!item) return acc

      const existingScope = acc.find((scope) => scope?.scopeId === item.scopeId)

      if (existingScope) {
        existingScope.excludedRequirements = [
          ...Array.from(
            new Set([
              ...existingScope.excludedRequirements,
              ...item.excludedRequirements,
            ])
          ),
        ]
      } else {
        acc.push(item)
      }

      return acc
    }, [])

    setDeselectedScopes(groupedScopes)
  }, [selected, scopes])

  const isPartiallySelected = (scopeId: number): boolean => {
    const groups = formattedBaseRequirements.find(
      (groupList) => groupList[0].scope?.scopeID === scopeId
    )
    if (!groups) return false

    const allReqs = groups.flatMap((group) =>
      group.requirements.map((r) => r.requirementBaseID)
    )
    const selectedCount = allReqs.filter((id) => selected.includes(id)).length

    return selectedCount > 0 && selectedCount < allReqs.length
  }

  const isGroupPartiallySelected = (groupID: number): boolean => {
    const group = formattedBaseRequirements
      .flat()
      .find((g) => g.groupID === groupID)

    if (!group) return false

    const allReqs = group.requirements.map((r) => r.requirementBaseID)
    const selectedCount = allReqs.filter((id) => selected.includes(id)).length

    return selectedCount > 0 && selectedCount < allReqs.length
  }

  if (isDataLoading) {
    return <BackdropProgress />
  }

  return (
    <Box className={classes.wrapper}>
      {type === 'localization' ? (
        <CompaniesSelectToRequirementsMappingComponent
          scopes={scopes}
          groupsPerScopeValues={formattedBaseRequirements}
          selected={selected}
          setSelected={setSelected}
          setDeselectedScopes={setDeselectedScopes}
        />
      ) : null}
      {header ? header : null}
      <SimpleTreeView
        multiSelect
        checkboxSelection
        onItemSelectionToggle={handleItemSelectionToggle}
        selectedItems={selectedItems}
      >
        {scopes.map((scope) => {
          const groups = formattedBaseRequirements.find(
            (groups) => groups[0]?.scope?.scopeID === scope?.scopeID
          )
          return (
            <TreeItem
              itemId={`${TreeItemType.SCOPE}-${scope?.scopeID}`}
              key={`${TreeItemType.SCOPE}-${scope?.scopeID}`}
              label={
                <Box
                  sx={{
                    color: isPartiallySelected(scope?.scopeID)
                      ? 'green'
                      : 'inherit',
                    fontWeight: isPartiallySelected(scope?.scopeID)
                      ? 'bold'
                      : 'normal',
                  }}
                >
                  {scope?.name}
                </Box>
              }
            >
              {groups?.map((group) => (
                <TreeItem
                  itemId={`${TreeItemType.GROUP}-${group.groupID}`}
                  key={`${TreeItemType.GROUP}-${group.groupID}`}
                  label={
                    <Box
                      sx={{
                        color: isGroupPartiallySelected(group.groupID)
                          ? 'green'
                          : 'inherit',
                        fontWeight: isGroupPartiallySelected(group.groupID)
                          ? 'bold'
                          : 'normal',
                      }}
                    >
                      {group.name}
                    </Box>
                  }
                >
                  {group.requirements.map((requirement) => (
                    <TreeItem
                      itemId={`${TreeItemType.REQUIREMENT}-${requirement.requirementBaseID}`}
                      key={`${TreeItemType.REQUIREMENT}-${requirement.requirementBaseID}`}
                      label={requirement.name}
                    />
                  ))}
                </TreeItem>
              ))}
            </TreeItem>
          )
        })}
      </SimpleTreeView>
    </Box>
  )
}

export const useStyles = makeStyles((theme) => {
  return {
    wrapper: {
      marginTop: theme.spacing(1),
    },
  }
})
